import { MatDialog } from '@angular/material/dialog';
import { Injectable, Optional } from '@angular/core';
import { ErrorDialogComponent } from 'app/shared/dialogs/error-dialog/error-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from 'app/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { catchError, lastValueFrom, Observable, of, switchMap } from 'rxjs';
import { ApiService } from 'app/shared/apis/api.service';
import { snakeCase } from 'lodash';
import { IFilterPagination } from 'app/shared/interfaces/filter-interface';
import { IPagination } from 'app/shared/interfaces/pagination.dto';

@Injectable({
    providedIn: 'root',
})
export class BaseService<T> {
    constructor(public api: ApiService, public _matDialog: MatDialog, @Optional() public endpoint?: string, @Optional() public name_id: string = null) {}

    isCreating(item: T): boolean {
        return !item[this.getIdName];
    }

    // Proxy handler para adicionar .promise a métodos que retornam Observable
    withPromise<U>(observable: Observable<U>): Observable<U> & { promise: Promise<U> } {
        const obs = observable as Observable<U> & { promise: Promise<U> };
        Object.defineProperty(obs, 'promise', {
            get: () => lastValueFrom(observable),
            enumerable: false,
            configurable: true,
        });
        return obs;
    }

    list(filter?: IFilterPagination<T>, reqOpts?: { headers?: { [name: string]: string } }) {
        const $request = this.api.getSub<IPagination<T>>(`${this.endpoint}/list`, filter, reqOpts);
        return this.withPromise($request);
    }

    create(item: T) {
        const $request = this.api.postSub<T>(`${this.endpoint}/create`, item).pipe(
            catchError((error) => {
                // Verificar se o erro tem opção de restore (usuário deletado)
                if (error.error?.canRestore && error.error?.deletedUserId) {
                    return this.handleRestoreError(error, item);
                }
                this.openDialogErro({
                    title: `Erro ao Cadastrar ${this.endpoint}!!`,
                    message: this.getErrorMessage(error),
                });
                throw error;
            }),
        );
        return this.withPromise($request);
    }

    private handleRestoreError(error: any, item: T): Observable<T> {
        const deletedUser = error.error?.deletedUser;
        const deletedUserId = error.error?.deletedUserId;
        const message = error.error?.message || 'Existe um usuário desativado com estes dados. Deseja reativá-lo?';
        const isUpdate = !!item[this.getIdName];

        if (this.endpoint === 'user' && deletedUser && !this.matchesUserCompositeKey(deletedUser, item)) {
            this.openDialogErro({
                title: `Erro ao ${isUpdate ? 'Atualizar' : 'Cadastrar'} user!!`,
                message:
                    'Existe um usuário desativado com e-mail ou documento semelhante, mas com tipo ou documento diferente. Crie uma nova conta ou ajuste os dados.',
            });
            throw error;
        }
        
        const dialogData: ConfirmDialogData = {
            title: 'Usuário Desativado Encontrado',
            message: message + (deletedUser ? `<br><br><strong>Usuário:</strong> ${deletedUser.name || deletedUser.email || deletedUser.doc_number}` : ''),
            confirmText: 'Reativar',
            cancelText: 'Cancelar',
        };

        const dialogRef = this._matDialog.open(ConfirmDialogComponent, {
            width: '500px',
            data: dialogData,
        });

        return dialogRef.afterClosed().pipe(
            switchMap((confirmed) => {
                if (confirmed && deletedUserId && this.endpoint === 'user') {
                    // Chamar o endpoint de restore
                    return this.api.putSub<T>(`${this.endpoint}/${deletedUserId}/restore`, {}).pipe(
                        switchMap((restoredUser) => {
                            // Após restaurar, atualizar o usuário restaurado com os dados do formulário
                            // Sempre usar update, pois o usuário já existe (foi restaurado)
                            const userToUpdate = { ...item, [this.getIdName]: deletedUserId };
                            return this.api.putSub<T>(`${this.endpoint}/${deletedUserId}`, userToUpdate);
                        }),
                        catchError((retryError) => {
                            // Se ainda der erro, mostrar mensagem
                            if (retryError.error?.canRestore) {
                                // Se ainda tem canRestore, significa que ainda há conflito
                                this.openDialogErro({
                                    title: 'Erro ao Salvar',
                                    message: this.getErrorMessage(retryError),
                                });
                            } else {
                                this.openDialogErro({
                                    title: `Erro ao ${isUpdate ? 'Atualizar' : 'Cadastrar'} ${this.endpoint}!!`,
                                    message: this.getErrorMessage(retryError),
                                });
                            }
                            throw retryError;
                        }),
                    );
                }
                // Se cancelou, lançar o erro original
                throw error;
            }),
        );
    }

    getById(id: string | number) {
        const $request = this.api.getSub<T>(`${this.endpoint}/${id}`).pipe(
            catchError((error) => {
                this.openDialogErro({
                    title: `Erro ao Buscar ${this.endpoint}!!`,
                    message: this.getErrorMessage(error),
                });
                throw error;
            }),
        );
        return this.withPromise($request);
    }

    update(item: T) {
        const id = item[this.getIdName];
        const $request = this.api.putSub<T>(`${this.endpoint}/${id}`, item).pipe(
            catchError((error) => {
                // Verificar se o erro tem opção de restore (usuário deletado)
                if (error.error?.canRestore && error.error?.deletedUserId) {
                    return this.handleRestoreError(error, item);
                }
                this.openDialogErro({
                    title: `Erro ao Atualizar ${this.endpoint}!!`,
                    message: this.getErrorMessage(error),
                });
                throw error;
            }),
        );
        return this.withPromise($request);
    }

    delete(item: T) {
        const id = item[this.getIdName];
        const $request = this.api.deleteSub<void>(`${this.endpoint}/${id}`).pipe(
            catchError((error) => {
                this.openDialogErro({
                    title: `Erro ao Deletar ${this.endpoint}!!`,
                    message: this.getErrorMessage(error),
                });
                throw error;
            }),
        );
        return this.withPromise($request);
    }

    openDialogErro(item: { title: string; message: string }): void {
        this._matDialog.open(ErrorDialogComponent, {
            panelClass: 'notification-dialog',
            width: '500px',
            data: item,
        });
    }

    getErrorMessage(error: any): string {
        return (
            error.error?.errors?.map((e: any) => e.descricao).join('<br />') ||
            error.error.message ||
            error.error.sqlMessage ||
            error.error.descricao ||
            'Erro desconhecido'
        );
    }

    private normalizeUserEmail(email: unknown): string | null {
        if (email == null || !String(email).trim()) return null;
        return String(email).trim();
    }

    private matchesUserCompositeKey(deletedUser: any, item: any): boolean {
        const companyId = item?.company_id ?? null;
        const deletedCompanyId = deletedUser?.company_id ?? null;
        return (
            deletedCompanyId === companyId &&
            this.normalizeUserEmail(deletedUser?.email) === this.normalizeUserEmail(item?.email) &&
            deletedUser?.type_user === item?.type_user &&
            (deletedUser?.doc_number ?? null) === (item?.doc_number ?? null)
        );
    }

    get getIdName(): string {
        return this.name_id || `${snakeCase(this.endpoint)}_id`;
    }
}
